import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import './App.css';
import OrderFormPage from './components/OrderFormPage';
import {
  matchPath,
  Navigate,
  Route,
  Routes,
  useLocation,
} from 'react-router-dom';
import OrderDetails from './components/OrderDetails';
import ReturnAndPaymentMethodPage from './components/ReturnAndPaymentMethodPage';
import ReturnPage from './components/ReturnPage';
import WithdrawalFormPage from './components/WithdrawalFormPage';
import WithdrawalReceiptPage from './components/WithdrawalReceiptPage';
import { ThemeProvider, useTheme } from './contexts/ThemeProvider';
import 'bootstrap-icons/font/bootstrap-icons.css';
import {
  API_URL,
  bindStartShop,
  ensureStartTexts,
  isKnownShopId,
  REFRESH_TIME,
  setLanguage,
  setShop,
  setToken,
  SHOP_ID,
  THEMES,
} from './utils/helpers';
import { resolveEntryLanguage } from './i18n/languages';
import { JwtToken } from './utils/types';
import { IframeResizer } from './components/IFrameResizer';
import { useTranslation } from 'react-i18next';
import {
  hasStandaloneQrFlag,
  isStandaloneQrLocation,
  hasHandoffFlag,
  isHandoffSearch,
} from './utils/qrEntry';
import {
  endStandaloneSession,
  hasStandaloneSession,
  installStandaloneSession,
  resetStandaloneSession,
  standaloneTheme,
} from './services/standaloneQrSession';
import {
  HeaderShop,
  StandaloneQrContext,
  StandaloneQrContextValue,
  StandaloneQrStatus,
  statusPageKey,
} from './contexts/StandaloneQrContext';
import { VerifiedShop } from './services/orderLookupService';
import PortalHeader from './components/PortalHeader';
import { refreshPortalSession } from './services/portalCredentials';
import HandoffUnavailable from './components/HandoffUnavailable';
import { handoffAccount } from './utils/handoffAccount';
import { isEmbedded } from './utils/embedding';

import { applyShopTheme } from './theme/applyShopTheme';
import {
  cachedCustomTheme,
  cachedPortalTheme,
  fetchPortalTheme,
  shopDomainOf,
} from './services/portalSettings';

// #493 (S3): the withdrawal pages (`/withdrawal/login`, form, receipt) keep
// their static texts
const isWithdrawalPath = (pathname: string): boolean =>
  pathname === '/withdrawal' || pathname.startsWith('/withdrawal/');

const ShopThemeLoader: React.FC<{ shopDomain: string; fallback: string }> = ({
  shopDomain,
  fallback,
}) => {
  const theme = useTheme();
  const latestTheme = useRef(theme);
  latestTheme.current = theme;

  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    void fetchPortalTheme(shopDomain, controller.signal).then(result => {
      if (!active || result.kind === 'failed') return;
      applyShopTheme(
        result.kind === 'set' ? (result.customTheme ?? null) : null
      );
      const nextTheme = result.kind === 'set' ? result.theme : fallback;
      if (nextTheme !== latestTheme.current.currentTheme) {
        latestTheme.current.setTheme(nextTheme);
      }
    });
    return () => {
      active = false;
      controller.abort();
    };
    // The shop comes from the entry URL and stays fixed for this visit.
  }, [shopDomain, fallback]);

  return null;
};

// #72: reload of a standalone QR session (nothing to restore it from).
// #110 (S5): on the design system, same copy and role as before
const StandaloneExpired: React.FC<{
  handoff: boolean;
  account: string | null;
}> = ({ handoff, account }) => {
  const { t } = useTranslation();
  const { state } = useLocation();
  return (
    <div className="xc-screen">
      {handoff ? (
        <HandoffUnavailable account={account ?? handoffAccount('', state)} />
      ) : (
        <div className="xc-notice xc-notice--warn" role="alert">
          {t('forms.qrStandaloneExpired')}
        </div>
      )}
    </div>
  );
};

const App: React.FC = () => {
  const { pathname, search, state: navigationState } = useLocation();
  const { i18n } = useTranslation(['translation', 'dynamic']);
  // #69: the status page (/return/{id}?sig=, opened from an email link) is
  // read-only and has no client JWT; it loads the labels of the shop from the
  // verified return itself (ReturnPage).
  const statusMatch = matchPath('/return/:id', pathname);
  const isStatusPage = statusMatch !== null;

  // #72: a parcel QR opened full screen (`/order-form-page?rq=` without proof
  // and shop; #154: an old `/withdrawal?rq=` link was canonicalized to it
  // before this module loaded). Decided once, from the entry URL (OrderFormPage then strips
  // `rq`); for the whole visit the shop comes only from the QR redemption and
  // nothing shop-, token- or theme-related is read from the shared storage.
  // A history entry of a standalone session (navigation-state flag) keeps
  // the mode across a reload; its session lived in memory only, so it cannot
  // be restored: the page fails closed before any request or storage read.
  const [handoff] = useState(
    () =>
      (isStandaloneQrLocation(pathname, search) && isHandoffSearch(search)) ||
      hasHandoffFlag(navigationState)
  );
  const [account] = useState(() =>
    handoff ? handoffAccount(search, navigationState) : null
  );
  const [standaloneEntry] = useState(() =>
    isStandaloneQrLocation(pathname, search)
  );
  // Decided per CURRENT history entry, not only at start-up: Back / Forward
  // into a flagged entry of a session this tab no longer holds (e.g. after a
  // reload on the signed status page, which is never flagged) fails closed.
  // The QR entry tab itself is exempt only while it has not redeemed yet
  // (pending / failure); once its session ended (status page) it expires too.
  const [standaloneState, setStandaloneState] = useState<{
    status: StandaloneQrStatus;
    shop: VerifiedShop | null;
  }>({ status: 'pending', shop: null });
  const entryExpired =
    hasStandaloneQrFlag(navigationState) &&
    !hasStandaloneSession() &&
    !(standaloneEntry && standaloneState.status !== 'success');
  const [startedExpired] = useState(entryExpired);
  const standalone = standaloneEntry || startedExpired;
  const [entrySearch] = useState(search);
  const requestedTheme = new URLSearchParams(entrySearch).get('theme');
  const urlTheme = THEMES.find(theme => theme.name === requestedTheme)?.name;
  const shopDomain = shopDomainOf(entrySearch);
  // #493 (S3): the start-page texts come from the shop of the entry URL, only
  // on the returns flow: never the standalone QR, the status page or
  // `/withdrawal/*`. Bound before the first render of the start form (its
  // render gate reads it).
  const [startShop] = useState(() => {
    const domain =
      standalone || isStatusPage || entryExpired || isWithdrawalPath(pathname)
        ? null
        : shopDomain;
    bindStartShop(domain);
    return domain;
  });
  const startTexts =
    startShop !== null &&
    !standalone &&
    !isStatusPage &&
    !entryExpired &&
    !isWithdrawalPath(pathname);
  const [entryTheme] = useState(() =>
    standalone
      ? undefined
      : (cachedPortalTheme(shopDomain) ?? urlTheme ?? 'version1')
  );
  // #189 (ra#499 S3): the cached colours and fonts of the shop, before the
  // first paint; ShopThemeLoader settles them. Same scope as the shop setting:
  // never the standalone QR, nor a status page without `?shop`.
  useState(() => {
    applyShopTheme(standalone ? null : cachedCustomTheme(shopDomain));
    return null;
  });
  // #105 (D7, review r3): top level = a viewport-height shell that pushes the
  // sticky action bar to the bottom of short pages; embedded stays
  // content-sized so IFrameResizer posts the content height
  const [embedded] = useState(isEmbedded);

  const succeed = useCallback((shop: VerifiedShop, token: string) => {
    installStandaloneSession(shop, token);
    setStandaloneState({ status: 'success', shop });
  }, []);
  const fail = useCallback(() => {
    resetStandaloneSession();
    setStandaloneState({ status: 'failure', shop: null });
  }, []);
  // #125: the shop of the signed return (ReturnPage), keyed by its link. On
  // the status page ONLY this identity is shown, and only while the current
  // route is that link: never the standalone QR shop (kept after the session
  // ended), never the shop of another return - not even on the first render
  // after a navigation, before ReturnPage's effect clears it.
  const [headerShop, setHeaderShop] = useState<HeaderShop | null>(null);
  const statusKey = isStatusPage
    ? statusPageKey(
        statusMatch.params.id,
        new URLSearchParams(search).get('sig')
      )
    : null;
  const statusPageShop =
    headerShop !== null && statusKey !== null && headerShop.key === statusKey
      ? headerShop.shop
      : null;
  const standaloneContext = useMemo<StandaloneQrContextValue>(
    () => ({
      active: standalone,
      handoff,
      account,
      ...standaloneState,
      succeed,
      fail,
      setHeaderShop,
    }),
    [standalone, standaloneState, succeed, fail, handoff, account]
  );

  useEffect(() => {
    if (!standalone) return;
    // PENDING: no shop, no token, no labels, no refresh until the QR is
    // redeemed; the entry language (below) applies without shop labels
    resetStandaloneSession();
    return () => resetStandaloneSession();
  }, [standalone]);

  // #146: both flows resolve the entry language once (URL, browser, English)
  // and apply it through the language coordinator.
  // Until it settles - on success and on failure alike - the app renders in
  // English. #64: a fresh iframe does not know its shop yet; the shop labels
  // are then loaded after POST /orders (OrderFormPage). #69: the status page
  // loads the labels of its verified shop itself.
  const [i18nReady, setI18nReady] = useState(false);
  const [entryIsStatusPage] = useState(isStatusPage);
  useEffect(() => {
    let active = true;
    const language = resolveEntryLanguage(
      entrySearch,
      navigator.languages ?? []
    );
    setLanguage(language, {
      withShopLabels:
        !standalone && !entryIsStatusPage && isKnownShopId(SHOP_ID),
      withStartTexts: startShop !== null,
    })
      .catch(error => console.warn(error, 'Entry language not applied.'))
      .finally(() => {
        if (active) setI18nReady(true);
      });
    return () => {
      active = false;
    };
    // decided once, from the entry URL (every dependency is fixed at entry)
  }, [entrySearch, standalone, entryIsStatusPage, startShop]);

  // #493 review r1: back on the returns flow (e.g. Back from the status page
  // after a language change there), reload the start texts of the language
  // when none are loaded or loading for the bound shop
  const currentLanguage = i18n.language;
  useEffect(() => {
    if (startTexts) ensureStartTexts();
  }, [startTexts, pathname, currentLanguage]);

  // bot P2: the submit opens the signed status page without unmounting App;
  // the session (refresh + client token) ends there.
  useEffect(() => {
    if (standalone && isStatusPage) endStandaloneSession();
  }, [standalone, isStatusPage]);

  useEffect(() => {
    if (standalone) return;
    const queryParams = new URLSearchParams(search);
    const showLanguageSelector = queryParams.get('show-language-selector');
    if (showLanguageSelector !== null) {
      localStorage.setItem('showSelector', showLanguageSelector);
    } else {
      localStorage.setItem('showSelector', 'true');
    }
  }, []);

  useEffect(() => {
    // #72: the full-screen QR session refreshes its own token (bound to that
    // session, see standaloneQrSession), never the one in the shared storage
    if (isStatusPage || standalone || entryExpired) return;
    const interval = setInterval(
      async () => {
        const existingJwt = localStorage.getItem('jwtToken');
        if (existingJwt !== null) {
          const newToken = await refreshToken(existingJwt);
          // #149: a lookup (return or withdrawal session) installed another token
          // while this refresh was running: never overwrite it
          if (localStorage.getItem('jwtToken') !== existingJwt) return;
          // #154 (Codex r2): the session record is rewritten whole first
          refreshPortalSession(existingJwt, newToken.access_token);
          localStorage.setItem('jwtToken', newToken.access_token);
          setToken(newToken.access_token);
          localStorage.setItem('shopId', newToken.shop.toString());
          setShop(newToken.shop);
        }
      },
      REFRESH_TIME * 60 * 1000
    );

    return () => clearInterval(interval);
  }, [isStatusPage, standalone, entryExpired]);

  const refreshToken = async (token: string): Promise<JwtToken> => {
    try {
      const response = await fetch(API_URL + '/refresh-token', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ` + token,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to refresh token');
      }

      return await response.json();
    } catch (error) {
      console.error('Error refreshing token:', error);
      throw error;
    }
  };

  // #67: keep the query (proof, shop, language, theme) on the redirect; a
  // prefixed storefront URL reaches the fallback route and the order form
  // reads proof and shop from the query.
  const orderFormRedirect = (
    <Navigate to={{ pathname: '/order-form-page', search }} replace />
  );

  const showLanguageSelector = standalone
    ? new URLSearchParams(entrySearch).get('show-language-selector') !== 'false'
    : localStorage.getItem('showSelector') !== 'false';

  return (
    <StandaloneQrContext.Provider value={standaloneContext}>
      <IframeResizer enabled={true}>
        <ThemeProvider
          entryTheme={entryTheme}
          fixedTheme={standalone ? standaloneTheme(entrySearch) : undefined}
        >
          {!standalone && shopDomain && (
            <ShopThemeLoader
              shopDomain={shopDomain}
              fallback={urlTheme ?? 'version1'}
            />
          )}
          {/* #105 (S1, plan D1): the portal root; every portal.css rule is
              scoped under it */}
          <div className={embedded ? 'xc-portal' : 'xc-portal xc-portal--top'}>
            <PortalHeader
              shop={
                entryExpired
                  ? null
                  : isStatusPage
                    ? statusPageShop
                    : standaloneState.shop
              }
              showLanguageSelector={showLanguageSelector}
              startTexts={startTexts}
              staticOnly={
                isStatusPage ||
                entryExpired ||
                (standalone && standaloneState.status !== 'success')
              }
            />
            <div className="App" data-i18n-ready={i18nReady ? 'true' : 'false'}>
              {entryExpired ? (
                <StandaloneExpired
                  handoff={handoff || hasHandoffFlag(navigationState)}
                  account={account}
                />
              ) : (
                <Routes>
                  {/* Redirect root to order lookup */}
                  <Route path="/" element={orderFormRedirect} />

                  {/* #154: the single entry (order search, manual or QR);
                      return-api routes it to a return or a withdrawal.
                      The old `/withdrawal` lookup is canonicalized to it at
                      start-up (src/startup/canonicalize.ts) */}
                  <Route path="/order-form-page" element={<OrderFormPage />} />

                  <Route
                    path="/withdrawal/login"
                    element={<OrderFormPage key="withdrawal-login" />}
                  />

                  {/* #149: the withdrawal declaration and its receipt */}
                  <Route
                    path="/withdrawal/form"
                    element={<WithdrawalFormPage />}
                  />
                  <Route
                    path="/withdrawal/:id"
                    element={<WithdrawalReceiptPage />}
                  />

                  {/* Order details page */}
                  <Route path="/order-details" element={<OrderDetails />} />

                  {/* Return method page */}
                  <Route
                    path="/methods"
                    element={<ReturnAndPaymentMethodPage />}
                  />

                  {/* Return summary page */}
                  <Route path="/return/:id" element={<ReturnPage />} />

                  {/* 404 fallback */}
                  <Route path="*" element={orderFormRedirect} />
                </Routes>
              )}
            </div>
          </div>
        </ThemeProvider>
      </IframeResizer>
    </StandaloneQrContext.Provider>
  );
};

export default App;
